import { Card, CardContent, CardHeader } from "@/components/layouts/Card";
import PageLoader from "@/components/loader/PageLoader";
import OptionListSelectionModal from "@/components/modals/OptionListSelectionModal";
import ReorderableList from "@/components/modals/ReorderableList";
import { useUser } from "@/contexts/UserContext";
import { OptionList, Product } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  faCircle,
  faImage,
  faPlus,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const AddProduct = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [product, setProduct] = useState<Product>({
    id: 0,
    name: "",
    image: "",
    color: "#ffffff",
    description: "",
    unit_price: "",
    promotion: "",
    in_stock: false,
    is_available: false,
    menu_display_name: "",
    category: null,
    product_option_lists: [],
    status: "",
    product_type: "",
  });

  const [categories, setCategories] = useState<Product[]>([]);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageBase64, setImageBase64] = useState<string>("");
  const [isOptionListModalOpen, setIsOptionListModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  const [imgExtension, setImgExtension] = useState("");

  useEffect(() => {
    const fetchAllCategories = async () => {
      if (loading || !axiosInstance || !user?.selectedBranch) return;
      try {
        const apiUrl =
          import.meta.env.VITE_APP_REDBIRDPOSBE_CATEGORY_INFORMATION ?? "";
        const url = `${apiUrl}?branch=${user.selectedBranch.id}&page=1&page_size=100`;

        const response = await axiosInstance.get(url);
        setCategories(response.data.results);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to fetch categories");
      }
    };

    fetchAllCategories();
  }, [axiosInstance, loading, user?.selectedBranch]);

  const fileToBase64 = (
    file: File,
    callback: (result: string | ArrayBuffer | null) => void,
  ): void => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => callback(reader.result);
    reader.onerror = (error) =>
      console.log("Error converting file to Base64:", error);
  };

  const colorOptions = [
    { label: "Red", value: "#ff0000" },
    { label: "Green", value: "#00ff00" },
    { label: "Blue", value: "#0000ff" },
    { label: "Yellow", value: "#ffff00" },
    { label: "Purple", value: "#800080" },
    { label: "Orange", value: "#ffa500" },
    { label: "Black", value: "#000000" },
  ];

  const productTypeOptions = [
    { label: "Physical Product", value: "Physical Product" },
    { label: "Service", value: "Service" },
  ];

  const handleColorChange = (value: string) => {
    setProduct({ ...product, color: value });
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.size > 20000000) {
        toast.error(
          "File size is too large. Please upload an image less than 20MB.",
        );
        return;
      }
      setImgExtension(file.name.split(".")[1]);
      setImagePreviewUrl(URL.createObjectURL(file));
      setIsImageUploaded(true);

      fileToBase64(file, (base64Result: string | ArrayBuffer | null) => {
        if (typeof base64Result === "string") {
          const base64Data = base64Result.split(",")[1];
          setImageBase64(base64Data);
        }
      });
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let product_status = "";

    if (product.product_type === "Service") {
      product_status = product.is_available ? "Available" : "Unavailable";
    } else {
      product_status = product.in_stock ? "In Stock" : "Out of Stock";
    }

    if (loading || !axiosInstance) return;
    const transformedProductOptionLists = product.product_option_lists.map(
      (optionList) => ({
        ...optionList,
        options: optionList.options.map((option: { id: any }) => option.id),
      }),
    );

    const productData = {
      ...product,
      image: imageBase64 ? imageBase64 : undefined,
      img_extension: imgExtension,
      unit_price: parseFloat(product.unit_price),
      branch: user?.selectedBranch?.id,
      sku: "1",
      category: selectedCategoryId,
      product_option_lists: transformedProductOptionLists,
      status: product_status,
    };

    try {
      const apiUrl = import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION;
      await axiosInstance.post(apiUrl, productData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      toast.success("Product added successfully");
      navigate("/menu/products");
    } catch (error) {
      toast.error("Failed to add product");
    }
  };

  const handleOptionListSave = (selectedOptionLists: OptionList[]) => {
    const updatedOptionLists = selectedOptionLists.map((optionList) => {
      const existingOptionList = product.product_option_lists.find(
        (existingList) => existingList.id === optionList.id,
      );

      return existingOptionList
        ? { ...existingOptionList, ...optionList }
        : optionList;
    });

    setProduct((prevProduct) => ({
      ...prevProduct,
      product_option_lists: updatedOptionLists,
    }));

    setIsOptionListModalOpen(false);
  };

  const handleRemoveOptionList = (optionList: OptionList) => {
    setProduct((prevProduct) => ({
      ...prevProduct,
      product_option_lists: prevProduct.product_option_lists.filter(
        (list) => list.id !== optionList.id,
      ),
    }));
  };

  const handleRemoveImage = () => {
    setImagePreviewUrl("");
    setIsImageUploaded(false);
    setImageBase64("");
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <h1 className="text-xl font-bold">
          {t("back.management.menu.product.addnewproduct")}
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.product.generalinfo")}
            </h2>
            <div className="-mx-2 flex flex-wrap">
              <div className="mb-4 w-full px-2 md:mb-0 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="name"
                >
                  {t("back.management.menu.product.productname")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  placeholder={t(
                    "back.management.menu.product.enterproductname",
                  )}
                  value={product.name}
                  onChange={(e) =>
                    setProduct({ ...product, name: e.target.value })
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>
              <div className="w-full px-2 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="posMenuColor"
                >
                  {t("back.management.menu.product.poscolor")}
                </label>
                <select
                  id="posMenuColor"
                  value={product.color}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                >
                  <option value="">
                    {t("back.management.menu.product.selectcolor")}
                  </option>
                  {colorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {product.color && (
                  <FontAwesomeIcon
                    icon={faCircle}
                    color={product.color}
                    className="mt-2"
                  />
                )}
              </div>
            </div>
            <div className="-mx-2 flex flex-wrap">
              <div className="mb-4 w-full px-2 md:mb-0 md:w-1/2">
                <label
                  className="mb-2 block text-sm font-bold text-gray-700"
                  htmlFor="menu_display_name"
                >
                  {t("back.management.menu.product.menuDisplayName")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder={t(
                    "back.management.menu.product.enterMenuDisplayName",
                  )}
                  value={product.menu_display_name}
                  required
                  onChange={(e) =>
                    setProduct({
                      ...product,
                      menu_display_name: e.target.value,
                    })
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
                />
              </div>
            </div>
            <div className="mt-4">
              <label
                className="mb-2 block text-sm font-bold text-gray-700"
                htmlFor="description"
              >
                {t("back.management.menu.product.description")}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                required
                placeholder={t("back.management.menu.product.enterdescription")}
                value={product.description}
                onChange={(e) =>
                  setProduct({ ...product, description: e.target.value })
                }
                className="block h-48 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
              />
            </div>
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.product.media")}
            </h2>
            <p className="mb-2 mt-4 block text-sm font-bold text-gray-700">
              {t("back.management.menu.product.uploadimage")}
            </p>
            <div
              className={`block w-full border-2 ${
                isImageUploaded ? "border-solid" : "border-dotted"
              } relative flex h-48 cursor-pointer items-center justify-center rounded-md border-gray-300 shadow-sm hover:border-gray-500`}
            >
              <input
                id="fileUpload"
                type="file"
                accept="image/*"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                onChange={handleFileChange}
              />
              {imagePreviewUrl ? (
                <>
                  <img
                    src={imagePreviewUrl}
                    alt="Preview"
                    className="max-h-full max-w-full p-2"
                  />
                  <button
                    onClick={handleRemoveImage}
                    className="absolute right-0 top-0 m-2 rounded-full bg-red-500 p-1 text-white"
                    style={{ width: "30px", height: "30px" }}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </>
              ) : (
                <div className="text-center">
                  <FontAwesomeIcon
                    icon={faImage}
                    size="2x"
                    className="mb-2 text-gray-400"
                  />
                  <p className="mb-2 text-gray-600">
                    {t("back.management.menu.product.uploadimagehint")}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.product.pricing")}
              <span className="text-red-500">*</span>
            </h2>
            <input
              type="number"
              placeholder={t("back.management.menu.product.baseprice")}
              value={product.unit_price}
              required
              onChange={(e) =>
                setProduct({ ...product, unit_price: e.target.value })
              }
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
            />
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.product.productType")}
              <span className="text-red-500">*</span>
            </h2>
            <select
              value={product.product_type}
              onChange={(e) =>
                setProduct({
                  ...product,
                  product_type: e.target.value as Product["product_type"],
                })
              }
              required
              className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
            >
              <option value="">
                {t("back.management.menu.product.selectProductType")}
              </option>
              {productTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {product.product_type === "Physical Product" ? (
              <div className="my-4 rounded-lg border border-gray-100 bg-white p-4">
                <h2 className="text-lg font-semibold">
                  {t("back.management.menu.product.inventory")}
                </h2>
                <div className="mt-1 flex items-center">
                  <label className="relative mr-2 inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      id="in_stock"
                      className="peer sr-only"
                      checked={product.in_stock}
                      onChange={(e) =>
                        setProduct({ ...product, in_stock: e.target.checked })
                      }
                    />
                    <div className="peer h-5 w-9 rounded-full bg-gray-200 transition-all duration-500 ease-in-out after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] hover:bg-gray-300 peer-checked:bg-red-500 peer-checked:after:translate-x-full peer-checked:after:border-white hover:peer-checked:bg-red-600 peer-focus:outline-0 peer-focus:ring-transparent"></div>
                  </label>
                  <label htmlFor="in_stock">Is product in stock?</label>
                </div>
              </div>
            ) : product.product_type === "Service" ? (
              <div className="my-4 rounded-lg border border-gray-100 bg-white p-4">
                <h2 className="text-lg font-semibold">
                  {t("back.management.menu.product.availability")}
                </h2>
                <div className="mt-1 flex items-center">
                  <label className="relative mr-2 inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      id="is_available"
                      className="peer sr-only"
                      checked={product.is_available}
                      onChange={(e) =>
                        setProduct({
                          ...product,
                          is_available: e.target.checked,
                        })
                      }
                    />
                    <div className="peer h-5 w-9 rounded-full bg-gray-200 transition-all duration-500 ease-in-out after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] hover:bg-gray-300 peer-checked:bg-red-500 peer-checked:after:translate-x-full peer-checked:after:border-white hover:peer-checked:bg-red-600 peer-focus:outline-0 peer-focus:ring-transparent"></div>
                  </label>
                  <label htmlFor="is_available">Is service available?</label>
                </div>
              </div>
            ) : null}
          </div>

          <div className="my-4 rounded-lg bg-white p-4 shadow">
            <h2 className="text-lg font-semibold">
              {t("back.management.menu.product.category")}
              <span className="text-red-500">*</span>
            </h2>
            <select
              value={selectedCategoryId || ""}
              onChange={(e) =>
                setSelectedCategoryId(
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              required
              className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring"
            >
              <option value="">
                {t("back.management.menu.product.selectcategory")}
              </option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <Card className="mb-4">
            <CardHeader className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {t("Linked OptionLists")}
              </h2>
              <button
                type="button"
                onClick={() => setIsOptionListModalOpen(true)}
                className="flex items-center rounded-md bg-gray-500 px-3 py-1 text-white"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t("Edit")}
              </button>
            </CardHeader>
            <CardContent>
              <ReorderableList
                items={product.product_option_lists}
                onReorder={(newOptions) => {
                  setProduct({ ...product, product_option_lists: newOptions });
                }}
                onRemove={handleRemoveOptionList}
                renderItem={(optionList) => optionList.name}
              />
            </CardContent>
          </Card>

          <div className="mt-4 flex justify-end">
            <Link
              to="/menu/products"
              className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.product.cancelbutton")}
            </Link>
            <button
              type="submit"
              className="rounded-md bg-red-500 px-4 py-2 text-white"
            >
              {t("back.management.menu.product.confirmbutton")}
            </button>
          </div>
        </form>

        <OptionListSelectionModal
          isOpen={isOptionListModalOpen}
          onClose={() => setIsOptionListModalOpen(false)}
          onSave={handleOptionListSave}
          initialSelectedOptionLists={product.product_option_lists}
        />
      </div>
    </PageLoader>
  );
};

export default AddProduct;
